import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './SponsorUploadModal.css';

function SponsorUploadModal({ onClose, onSuccess }) {
  const [sponsorLogos, setSponsorLogos] = useState([]);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [adminPassword, setAdminPassword] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSponsors();
  }, []);

  useEffect(() => {
    const urls = Array.from(files).map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [files]);

  const fetchSponsors = async () => {
    try {
      const response = await api.get('/settings/sponsors');
      setSponsorLogos(response.data.logos || []);
    } catch {
      // non-fatal
    }
  };

  const handleFiles = (incoming) => {
    const imageFiles = Array.from(incoming).filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) {
      setMessage('Please select image files only.');
      setMessageType('error');
      return;
    }
    setFiles(imageFiles);
    setMessage('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (!adminPassword) { setMessage('Admin password is required.'); setMessageType('error'); return; }
    if (!files.length) { setMessage('Select at least one image first.'); setMessageType('error'); return; }

    setIsUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('password', adminPassword);
      files.forEach((f) => formData.append('files', f));
      const response = await api.post('/settings/sponsors', formData);
      setSponsorLogos(response.data.logos || []);
      setFiles([]);
      setAdminPassword('');
      setMessage(`${files.length} logo${files.length > 1 ? 's' : ''} uploaded successfully.`);
      setMessageType('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Upload failed.');
      setMessageType('error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (logoUrl) => {
    if (!adminPassword) { setMessage('Enter admin password before removing a logo.'); setMessageType('error'); return; }
    try {
      const formData = new FormData();
      formData.append('password', adminPassword);
      formData.append('logo_url', logoUrl);
      const response = await api.post('/settings/sponsors/remove', formData);
      setSponsorLogos(response.data.logos || []);
      setMessage('Logo removed.');
      setMessageType('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to remove logo.');
      setMessageType('error');
    }
  };

  return (
    <div className="sponsor-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sponsor-modal">
        <div className="sponsor-modal-header">
          <h2>Sponsor Logos</h2>
          <button className="sponsor-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="sponsor-modal-body">
          {/* Admin password */}
          <div className="sponsor-field">
            <label>Admin Password</label>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Required for upload and delete"
              autoComplete="new-password"
            />
          </div>

          {/* Current logos */}
          {sponsorLogos.length > 0 && (
            <div className="sponsor-section">
              <h3>Current Logos ({sponsorLogos.length})</h3>
              <div className="sponsor-grid">
                {sponsorLogos.map((url, i) => (
                  <div key={`${url}-${i}`} className="sponsor-thumb">
                    <img src={url} alt={`Sponsor ${i + 1}`} />
                    <button
                      className="sponsor-remove-btn"
                      onClick={() => handleRemove(url)}
                      title="Remove this logo"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload area */}
          <div className="sponsor-section">
            <h3>Upload New Logos</h3>
            <div
              className={`sponsor-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {previews.length > 0 ? (
                <div className="sponsor-grid">
                  {previews.map((src, i) => (
                    <div key={i} className="sponsor-thumb">
                      <img src={src} alt={`Preview ${i + 1}`} />
                      <span className="sponsor-thumb-name">{files[i]?.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sponsor-dropzone-hint">
                  <span className="sponsor-drop-icon">⬆</span>
                  <p>Drag &amp; drop images here, or click to select</p>
                  <p className="sponsor-hint-sub">JPG, PNG, WebP, GIF — max 5 MB each</p>
                </div>
              )}
            </div>
          </div>

          {message && <p className={`sponsor-message ${messageType}`}>{message}</p>}
        </div>

        <div className="sponsor-modal-footer">
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={isUploading || !files.length}
          >
            {isUploading ? 'Uploading...' : `Upload ${files.length ? `(${files.length})` : ''}`}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default SponsorUploadModal;
